import { useEffect, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { STABLE_EMPTY_OBJECT } from '../../common/utils';
import { type OnEvent, type RemotePlugin } from '../../types';
import AtlassianSystemRemoteFrontendSdk from '../remote-sdk';
import useRemoteModuleLoader from '../use-remote-module-loader';

const preloadedModuleMap = new WeakMap<RemotePlugin, AtlassianSystemRemoteFrontendSdk>();

/**
 * Create an SDK instance for a given plugin aand cache it in the WeakMap so that if a plugin is no longer referenced
 * we clean up the sdk from the cache
 *
 * @param remotePlugin
 */
const cacheAndGetSdkForPlugin = (remotePlugin: RemotePlugin) => {
	const cachedSdk = preloadedModuleMap.get(remotePlugin);
	if (cachedSdk) {
		return cachedSdk;
	}

	const sdk = new AtlassianSystemRemoteFrontendSdk(remotePlugin);
	preloadedModuleMap.set(remotePlugin, sdk);

	return sdk;
};

/**
 * Preload a plugin by instantiating it and firing off the request for the module in the registry
 *
 * @param remotePlugin
 * @param options
 */
const useRemoteModule = (
	remotePlugin: RemotePlugin,
	options: {
		analyticsChannel?: string;
		onError?: (error: Error) => void;
		onEvent?: OnEvent;
	} = STABLE_EMPTY_OBJECT,
) => {
	const remoteFrontendSdk = cacheAndGetSdkForPlugin(remotePlugin);
	const experience = remoteFrontendSdk.renderUfoExperience;
	const moduleName = remoteFrontendSdk.getModuleName();

	useEffect(() => {
		if (experience.state.id === 'NOT_STARTED') {
			experience.start();
		}
	}, [experience]);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'rendered',
			actionSubject: 'remoteModule',
			actionSubjectId: 'useRemoteModule',
			attributes: {
				dimensions: remotePlugin.dimensions,
				plugin: remotePlugin.constructor.name,
			},
		});

		analyticsEvent.fire(options?.analyticsChannel);
	}, [createAnalyticsEvent, remotePlugin, options.analyticsChannel]);

	const optionsWithError = useMemo(() => {
		const onError = (error: Error) => {
			void experience.failure({
				metadata: {
					moduleName,
				},
			});
			options?.onError?.(error);
		};

		return {
			...options,
			onError,
		};
	}, [experience, moduleName, options]);

	useRemoteModuleLoader(remoteFrontendSdk, optionsWithError);

	return remoteFrontendSdk;
};

export default useRemoteModule;
